import { Box, Chip, Skeleton, Typography } from '@mui/material';
import {
    PageHeader,
    PageMeta,
} from '../../../../components/common/page/PageHeader';
import type { Change } from '../../event/chart/changeOverRange';
import { formatValue } from '../../event/chart/format';
import { AssetImage } from '../../components/AssetImage';
import { BIG_VALUE_SX, BigNumber } from '../../components/BigNumber';
import type { AssetModel } from '../model/types';
import { AboutBox, AboutToggle, hasAbout, useAboutOpen } from './AboutBox';
import type { PriceFigures } from './priceFigures';

interface AssetHeaderProps {
    asset: AssetModel | null;
    loading: boolean;
    figures: PriceFigures;
    candidateCount: number;
    /** The price's change over the shown range. */
    change: Change | null;
    rangeLabel: string;
}

export function AssetHeader({
    asset,
    loading,
    figures,
    candidateCount,
    change,
    rangeLabel,
}: AssetHeaderProps) {
    const [aboutOpen, toggleAbout] = useAboutOpen();

    if (!asset) {
        return loading ? (
            <Skeleton variant="rounded" width="100%" height={56} />
        ) : null;
    }

    return (
        <Box>
            <PageHeader
                wrap
                leading={
                    <AssetImage
                        assetType={asset.assetType}
                        ticker={asset.ticker}
                        size={40}
                    />
                }
                title={asset.title}
                meta={
                    <>
                        <Chip
                            size="small"
                            label={asset.assetType}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '11px' }}
                        />
                        <PageMeta>{asset.ticker}</PageMeta>
                        {asset.categories.map(category => (
                            <PageMeta key={category}>{category}</PageMeta>
                        ))}
                        <PageMeta>
                            {candidateCount} candidate event
                            {candidateCount !== 1 ? 's' : ''}
                        </PageMeta>
                        {hasAbout(asset) && (
                            <AboutToggle
                                open={aboutOpen}
                                onToggle={toggleAbout}
                            />
                        )}
                    </>
                }
            >
                <BigNumber
                    value={
                        <Typography
                            component="span"
                            // the mockup's price is 38 px, a probability 40
                            sx={{
                                ...BIG_VALUE_SX,
                                fontSize: { xs: '38px', md: '24px' },
                            }}
                        >
                            {formatValue('price', figures.last)}
                        </Typography>
                    }
                    change={change}
                    rangeLabel={rangeLabel}
                    sx={{
                        flexBasis: { xs: '100%', md: 'auto' },
                        flexShrink: 0,
                    }}
                />
            </PageHeader>
            {hasAbout(asset) && <AboutBox asset={asset} open={aboutOpen} />}
        </Box>
    );
}
